import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { Field, Chip, RadioPill, FormSection, SuccessPanel } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

const DONATION_TYPES = ["Furniture", "Household essentials", "Home décor", "Multiple types of items"];

const ITEM_OPTIONS = [
  "Couch / loveseat", "Living room chair", "Recliner", "Dining table", "Dining chairs",
  "Bed frame", "Mattress (clean condition)", "Dresser", "Nightstand", "Coffee table",
  "End table", "Desk", "Office chair", "Bookshelf", "Storage furniture",
  "Children's furniture", "Lamp", "Small kitchen appliance", "Dishes / dinnerware",
  "Pots & pans", "Bedding / sheets / blankets", "Towels / linens", "Rug",
  "Curtains", "Wall décor / mirror", "Other",
];

const CONDITIONS = ["Like new", "Gently used", "Good", "Fair — has wear", "Needs minor repair"];

const initialForm = {
  full_name: "", phone: "", email: "", preferred_contact: "", city: "", zip_code: "",
  donation_types: [], items: [], description: "", quantity: "", condition: "", issues: "",
  can_drop_off: "", needs_pickup: "", pickup_address: "", stairs: "", elevator: "",
  items_location: "", removal_deadline: "", preferred_pickup_time: "", special_instructions: "",
};

export default function DonateItems() {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleList = (key, val) =>
    setForm({ ...form, [key]: form[key].includes(val) ? form[key].filter((v) => v !== val) : [...form[key], val] });

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 6) {
      toast.error("You can upload up to 6 photos.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      files.forEach((f) => data.append("files", f));
      const res = await api.post("/donations/photos", data, { headers: { "Content-Type": "multipart/form-data" } });
      const newPhotos = res.data.photos.map((path, i) => ({
        path,
        preview: URL.createObjectURL(files[i]),
      }));
      setPhotos([...photos, ...newPhotos]);
      toast.success(`${newPhotos.length} photo(s) uploaded.`);
    } catch (err) {
      toast.error(formatApiError(err, "Photo upload failed. Please try again."));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/donations", { ...form, photos: photos.map((p) => p.path) });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-40 sm:px-6">
        <SuccessPanel
          testid="donation-success-message"
          title="THANK YOU FOR HELPING CREATE A FRESH START!"
          message="We will review your donation and contact you about next steps. Fresh Start Furnishings may accept or decline donations based on condition, safety, current needs, storage space, and transportation availability."
        />
      </div>
    );
  }

  return (
    <div data-testid="donate-items-page">
      <PageHero overline="Donate Items" title="GIVE YOUR FURNITURE" italic="a second purpose." testid="donate-items-headline">
        Tell us what you have, share a few photos, and we'll coordinate pickup or drop-off across Glendale and the
        West Valley.
      </PageHero>

      <form onSubmit={submit} className="mx-auto max-w-4xl space-y-8 px-4 py-16 sm:px-6" data-testid="donation-form">
        <FadeUp>
          <FormSection number="01" title="Donor Information">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full name" required testid="donor-name-field">
                <input data-testid="donor-name-input" required className="input-field" value={form.full_name} onChange={set("full_name")} />
              </Field>
              <Field label="Phone number" required testid="donor-phone-field">
                <input data-testid="donor-phone-input" required type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
              </Field>
              <Field label="Email address" required testid="donor-email-field">
                <input data-testid="donor-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Preferred contact method" testid="donor-contact-method-field">
                <select data-testid="donor-contact-method-select" className="input-field" value={form.preferred_contact} onChange={set("preferred_contact")}>
                  <option value="">Select…</option>
                  <option>Call</option><option>Text</option><option>Email</option>
                </select>
              </Field>
              <Field label="City" testid="donor-city-field">
                <input data-testid="donor-city-input" className="input-field" value={form.city} onChange={set("city")} />
              </Field>
              <Field label="ZIP code" testid="donor-zip-field">
                <input data-testid="donor-zip-input" className="input-field" maxLength={5} value={form.zip_code} onChange={set("zip_code")} />
              </Field>
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="02" title="What Would You Like to Donate?">
            <div>
              <p className="mb-3 text-sm font-semibold text-olive">Donation type</p>
              <div className="flex flex-wrap gap-2">
                {DONATION_TYPES.map((t) => (
                  <Chip key={t} testid={`donation-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                    checked={form.donation_types.includes(t)} onToggle={() => toggleList("donation_types", t)} />
                ))}
              </div>
            </div>
            <div>
              <p className="mb-3 text-sm font-semibold text-olive">Specific items</p>
              <div className="flex flex-wrap gap-2">
                {ITEM_OPTIONS.map((t) => (
                  <Chip key={t} testid={`item-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                    checked={form.items.includes(t)} onToggle={() => toggleList("items", t)} />
                ))}
              </div>
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="03" title="Item Details">
            <Field label="Description" testid="item-description-field">
              <textarea data-testid="item-description-input" rows={3} className="input-field" placeholder="Tell us about the items — sizes, colors, sets…" value={form.description} onChange={set("description")} />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Quantity" testid="item-quantity-field">
                <input data-testid="item-quantity-input" className="input-field" placeholder="e.g. 3 pieces" value={form.quantity} onChange={set("quantity")} />
              </Field>
              <Field label="Condition" testid="item-condition-field">
                <select data-testid="item-condition-select" className="input-field" value={form.condition} onChange={set("condition")}>
                  <option value="">Select…</option>
                  {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Any stains, damage, missing parts, or issues" testid="item-issues-field">
              <textarea data-testid="item-issues-input" rows={2} className="input-field" placeholder="Be honest — it helps us plan." value={form.issues} onChange={set("issues")} />
            </Field>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="04" title="Photos of Your Items">
            <p className="text-sm text-moss">Upload up to 6 photos so we can review your donation faster.</p>
            <div className="flex flex-wrap gap-4">
              {photos.map((p, i) => (
                <div key={p.path} className="relative" data-testid={`photo-preview-${i}`}>
                  <img src={p.preview} alt={`Donation item ${i + 1}`} className="h-24 w-24 rounded-xl border border-linen object-cover" />
                  <button type="button" data-testid={`photo-remove-${i}`} aria-label="Remove photo"
                    onClick={() => setPhotos(photos.filter((x) => x.path !== p.path))}
                    className="absolute -right-2 -top-2 rounded-full bg-burgundy p-1 text-cream">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <label data-testid="photo-upload-label" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-linen text-sage transition-colors hover:border-olive hover:text-olive">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  <span className="text-[10px] font-medium">{uploading ? "Uploading" : "Add photos"}</span>
                  <input data-testid="photo-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} disabled={uploading} />
                </label>
              )}
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="05" title="Pickup or Drop-Off">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Can you drop the items off?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`drop-off-${v.toLowerCase()}`} label={v} value={v} selected={form.can_drop_off} onSelect={(val) => setForm({ ...form, can_drop_off: val })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Do you need a pickup?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`pickup-needed-${v.toLowerCase()}`} label={v} value={v} selected={form.needs_pickup} onSelect={(val) => setForm({ ...form, needs_pickup: val })} />
                  ))}
                </div>
              </div>
              <Field label="Pickup address" testid="pickup-address-field">
                <input data-testid="pickup-address-input" className="input-field" value={form.pickup_address} onChange={set("pickup_address")} />
              </Field>
              <Field label="Where are the items located?" testid="items-location-field">
                <input data-testid="items-location-input" className="input-field" placeholder="e.g. garage, 2nd floor apartment" value={form.items_location} onChange={set("items_location")} />
              </Field>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Are there stairs?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`stairs-${v.toLowerCase()}`} label={v} value={v} selected={form.stairs} onSelect={(val) => setForm({ ...form, stairs: val })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Is there an elevator?</p>
                <div className="flex gap-2">
                  {["Yes", "No", "N/A"].map((v) => (
                    <RadioPill key={v} testid={`elevator-${v.toLowerCase().replace("/", "")}`} label={v} value={v} selected={form.elevator} onSelect={(val) => setForm({ ...form, elevator: val })} />
                  ))}
                </div>
              </div>
              <Field label="When do the items need to be removed?" testid="removal-deadline-field">
                <input data-testid="removal-deadline-input" className="input-field" placeholder="e.g. by end of month" value={form.removal_deadline} onChange={set("removal_deadline")} />
              </Field>
              <Field label="Preferred pickup date/time" testid="pickup-time-field">
                <input data-testid="pickup-time-input" className="input-field" placeholder="e.g. Saturday morning" value={form.preferred_pickup_time} onChange={set("preferred_pickup_time")} />
              </Field>
            </div>
            <Field label="Special pickup instructions" testid="special-instructions-field">
              <textarea data-testid="special-instructions-input" rows={2} className="input-field" value={form.special_instructions} onChange={set("special_instructions")} />
            </Field>
            <p data-testid="donation-disclaimer" className="rounded-xl border border-burgundy/25 bg-burgundy/5 p-4 text-xs leading-relaxed text-burgundy">
              Fresh Start Furnishings may accept or decline donations based on condition, safety, current needs,
              storage space, and transportation availability.
            </p>
          </FormSection>
        </FadeUp>

        <FadeUp className="text-center">
          <button type="submit" data-testid="donate-items-form-submit-button" disabled={submitting}
            className="btn-accent !px-12 !py-4 text-base disabled:opacity-60">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {submitting ? "Submitting…" : "Submit My Donation"}
          </button>
        </FadeUp>
      </form>
    </div>
  );
}
